/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("ActionApplicationService", ["ApplicationServiceBase", "ApplicationServiceFactory"], function (context, cls) {
  /**
   * @class ActionApplicationService
   * @memberOf classes
   * @extends classes.ApplicationServiceBase
   */
  class ActionApplicationService extends cls.ApplicationServiceBase /** @lends classes.ActionApplicationService.prototype */ {
    /**
     * list of local actions with corresponding key accelerator
     * @type {Object}
     */
    /* jshint ignore:start */
    static _localActions = {
      nextfield: "tab",
      prevfield: "shift-tab",
      nextrow: "down",
      prevrow: "up",
      firstrow: "home",
      lastrow: "end",
      nextpage: "next",
      prevpage: "prior",
      nexttab: "control-tab",
      prevtab: "control-shift-tab",
      guisnapshot: "alt-f12"
    };
    /* jshint ignore:end */

    /**
     * Returns local action names
     *  @returns {String[]}
     */
    static getLocalActionNames() {
      return Object.keys(this._localActions);
    }

    /**
     * Returns local action accelerators
     *  @returns {String[]}
     */
    static getLocalActionAccelerators() {
      return Object.values(this._localActions);
    }

    /**
     * Returns default keyboard accelerator for a local action
     * @param {string} actionName
     * @returns {?string} accelerator
     */
    static getLocalActionAccelerator(actionName) {
      return this._localActions[actionName];
    }

    /** Returns local action name for an accelerator
     * @param {string} acc
     * @returns {?string} actionName
     */
    static getLocalActionName(acc) {
      const accelerators = this.getLocalActionAccelerators();
      const names = this.getLocalActionNames();
      for (let i = 0; i < accelerators.length; ++i) {
        if (acc === accelerators[i]) {
          return names[i];
        }
      }
      return null;
    }

    /**
     * Browser native actions
     * @type {String[]}
     */
    /* jshint ignore:start */
    static browserNativeActions = ["editcopy", "editcut", "editpaste"];
    /* jshint ignore:end */

    /**
     * static list of special actions
     * @type {Map<string, Object>}
     */
    /* jshint ignore:start */
    static specialActions = new Map();
    /* jshint ignore:end */

    /**
     * Add special action in the list
     * @param {string} name - action name
     * @param {Object} ctor - action class typeref
     */
    static registerSpecialAction(name, ctor) {
      this.specialActions.set(name, ctor);
    }

    /**
     * @inheritDoc
     */
    constructor(app) {
      super(app);
      /**
       * List of interrupt widgets
       * @type {Array}
       */
      this._interruptWidgets = [];
      /**
       * list of actions defaults
       * @type {Map<string, classes.ActionDefaultNode>}
       */
      this._actionDefaults = new Map();
      /**
       * List of bound actions filtered by browser key
       * @type {Map<string, Array>}
       */
      this._boundActions = new Map();
      /**
       * list of actions
       * @type {Map<string, classes.ActionNode>}
       */
      this._actions = new Map();
      /**
       * list of special actions
       * @type {Map<string, Object>}
       */
      this._specialActions = new Map();
    }

    /**
     * @inheritDoc
     */
    destroy() {
      this._specialActions.clear();
      this._actions.clear();
      this._actionDefaults.clear();
      this._boundActions.clear();

      this._interruptWidgets = null;
      this._specialActions = null;
      this._actions = null;
      this._actionDefaults = null;
      this._boundActions = null;

      super.destroy();
    }

    /**
     * Add a new action to this App Service
     * @param {classes.ActionNode} action node
     */
    registerAction(action) {
      let actionName = action.attribute("name");
      this._actions.set(actionName, action);

      this._bindAccelerators(action);
      if (cls.ActionApplicationService.specialActions.has(actionName)) {
        let actionConstructor = cls.ActionApplicationService.specialActions.get(actionName);
        this._specialActions.set(actionName, new actionConstructor(this));
      }
      if (actionName === "interrupt") {
        for (const element of this._interruptWidgets) {
          element.setInterruptable(false);
        }
      }

      let applicationWidget = this._application.getUI().getWidget();

      // Do not add to contextMenu/rowbound if not an Action/MenuAction
      if (applicationWidget && ["Action", "MenuAction"].includes(action.getTag())) {
        // Add action to application contextMenu
        if (applicationWidget.getContextMenu()) {
          let actionOrder = action.getIndex();
          let contextMenuWidget = applicationWidget.getContextMenu();
          let contextMenu = action.attribute("contextMenu");
          let addInCtxMenu = contextMenu === "yes" || contextMenu === "auto";
          if (addInCtxMenu) {
            let actionText = action.attribute("text").replaceAll(/&(.)/g, "$1"); // Filter ampersand
            contextMenuWidget.addAction(actionName, actionText, action.resource("image"), action.attribute("acceleratorName").toString(), {
              clickCallback: function () {
                contextMenuWidget.hide();
                action.getWidget().emit(gbc.constants.widgetEvents.click);
              },
              hidden: action.attribute("hidden"),
              order: actionOrder
            });
          }
        }

        // Add action to application rowBound
        if (applicationWidget.getRowBoundMenu()) {
          let actionOrder = action.getIndex(),
            rowBoundWidget = applicationWidget.getRowBoundMenu(),
            addInRowBound = action.attribute("context") === "row";

          if (addInRowBound) {
            let actionText = action.attribute("text").replaceAll(/&(.)/g, "$1"); // Filter ampersand
            rowBoundWidget.addAction(
              actionName,
              actionText === "" ? actionName : actionText,
              action.resource("image"),
              action.attribute("acceleratorName").toString(),
              {
                clickCallback: function () {
                  rowBoundWidget.hide();
                  action.execute();
                },
                hidden: action.attribute("hidden"),
                order: actionOrder
              }
            );
          }
        }
      }

      // back action
      // Back is displayed:
      //   - In the chromebar (NavigationButton) when the chromebar is visible (regardless of defaultView).
      //   - In the action panel only when defaultView=yes AND actionPanelPosition <> "chrome" (GBC-5809 rule 1.1).
      //     When defaultView is not "yes", we hide the panel widget with the always-hidden CSS class — it cannot
      //     be undone if defaultView later changes, but defaultView is set at action declaration and does not change.
      if (this.isBackAction(action) && context.MainContainerService.isChromeBarVisible()) {
        const activateBackAction = () => {
          if (action.getWidget()) {
            context.ChromeActionService.addBackAction(action.getWidget(), () => {
              this.executeAction(action, { sendValue: true });
            });
          }
        };
        const disableBackAction = () => {
          context.ChromeActionService.removeBackAction(action.getWidget());
        };

        // Hide back from the action panel unless defaultView=yes (GBC-5809 rule 1.1). The check on parent
        // widget preserves visibility in popup/dialog menu styles where defaultView is ignored (GBC-600).
        if (!action.getWidget().getParentWidget().isDropDown && action.attribute("defaultView") !== context.constants.viewType.showAlways) {
          if (!action.getWidget().hasClass("always-hidden")) {
            action.getWidget().addClasses("always-hidden");
          }
        }

        action.addListener(
          context.constants.widgetEvents.activate,
          applicationWidget.when(context.constants.widgetEvents.activate, activateBackAction)
        );
        action.addListener(context.constants.widgetEvents.disable, applicationWidget.when(context.constants.widgetEvents.disable, disableBackAction));
        // Nav button must follow the action's own state (active + hidden) but NOT defaultView, which only
        // governs the panel widget. With defaultView=no + chromebar visible, back must stay in the chromebar
        // (GBC-5809 rule 2.1), so we check `action.attribute("hidden")` directly rather than `widget.isVisible()`.
        action.getWidget().when(context.constants.widgetEvents.visibilityChange, () => {
          const widget = action.getWidget();
          const isUserHidden = action.isAttributePresent("hidden") && Boolean(action.attribute("hidden"));
          if (widget?.isEnabled() && !isUserHidden) {
            activateBackAction();
          } else {
            disableBackAction();
          }
        });
        action.getWidget().emit(context.constants.widgetEvents.visibilityChange);
        // chrome action
      } else if (action.getController().isInChromeBar()) {
        const app = context.SessionService.getCurrent().getCurrentApplication();
        app.scheduler.callbackCommand(() => {
          if (action.getWidget()) {
            context.ChromeActionService.addChromeAction(action.getWidget());
          }
        }, true);

        action.addListener(
          context.constants.widgetEvents.activate,
          applicationWidget.when(context.constants.widgetEvents.activate, () => {
            if (action.getWidget()) {
              context.ChromeActionService.addChromeAction(action.getWidget());
            }
          })
        );
        action.addListener(
          context.constants.widgetEvents.disable,
          applicationWidget.when(context.constants.widgetEvents.disable, () => {
            if (action.getWidget()) {
              context.ChromeActionService.removeChromeAction(action.getWidget());
            }
          })
        );
      }
    }

    /**
     * Return true if action could be considered as a back action
     * @param {ActionNode} actionNode
     * @return {boolean}
     */
    isBackAction(actionNode) {
      return actionNode.attribute("name") === "back" && !actionNode.getAncestor("Window").isModal();
    }

    /**
     * Add a new actionDefault to this App Service
     * @param {classes.NodeBase} action node
     */
    registerActionDefault(action) {
      this._actionDefaults.set(action.attribute("name"), action);

      this._bindAccelerators(action);
    }

    /**
     * Bind an action to VM accelerators
     * @param {classes.NodeBase} action node
     */
    _bindAccelerators(action) {
      // TODO : why do we need to convert VMKey when storing accelerators since we are retrieving them in scheduler doing the opposite ? old implementation ?
      const key1 = cls.KeyboardHelper.convertVMKeyToBrowserKey(action.attribute("acceleratorName"));
      const key2 = cls.KeyboardHelper.convertVMKeyToBrowserKey(action.attribute("acceleratorName2"));
      const key3 = cls.KeyboardHelper.convertVMKeyToBrowserKey(action.attribute("acceleratorName3"));
      const key4 = cls.KeyboardHelper.convertVMKeyToBrowserKey(action.attribute("acceleratorName4"));

      if (key1) {
        this._bindAccelerator(key1, action);
      }
      // we make sure to not bind same action two times :
      // for example, Enter and Return VM accelerators are both considered as Enter key from browser
      // so we only have to bind one action to this key even if there are two accelerators defined on it
      if (key2 && key2 !== key1) {
        this._bindAccelerator(key2, action);
      }
      if (key3 && key3 !== key1 && key3 !== key2) {
        this._bindAccelerator(key3, action);
      }
      if (key4 && key4 !== key1 && key4 !== key2 && key4 !== key3) {
        this._bindAccelerator(key4, action);
      }
    }

    /**
     * Bind action to browser key
     * @param {string} key - DOM key combination
     * @param {classes.NodeBase} action node
     */
    _bindAccelerator(key, action) {
      //erase previous action to keep the last one
      this._boundActions.set(key, []);
      const actions = this._boundActions.get(key);
      if (!actions.includes(action._id)) {
        // push new action in the list
        actions.push(action._id);
      }
    }

    /**
     * Unbind an action from VM accelerators
     * @param {classes.NodeBase} action node
     */
    _unbindAccelerators(action) {
      // Remove all accelerator bound for this action...
      const key1 = cls.KeyboardHelper.convertVMKeyToBrowserKey(action.attribute("acceleratorName"));
      const key2 = cls.KeyboardHelper.convertVMKeyToBrowserKey(action.attribute("acceleratorName2"));
      const key3 = cls.KeyboardHelper.convertVMKeyToBrowserKey(action.attribute("acceleratorName3"));
      const key4 = cls.KeyboardHelper.convertVMKeyToBrowserKey(action.attribute("acceleratorName4"));

      if (key1) {
        this._unbindAccelerator(key1, action);
      }
      // we make sure to not unbind same action two times :
      // for example, Enter and Return VM accelerators are both considered as Enter key from browser
      // so we only have to unbind one action from this key even if there are two accelerators defined on it
      if (key2 && key2 !== key1) {
        this._unbindAccelerator(key2, action);
      }
      if (key3 && key3 !== key1 && key3 !== key2) {
        this._unbindAccelerator(key3, action);
      }
      if (key4 && key4 !== key1 && key4 !== key2 && key4 !== key3) {
        this._unbindAccelerator(key4, action);
      }
    }

    /**
     * Unbind action from browser key
     * @param {string} key browser key
     * @param {classes.NodeBase} action node
     */
    _unbindAccelerator(key, action) {
      const keyActionIdArray = this._boundActions.get(key);
      if (keyActionIdArray) {
        // remove action id from our active actions list
        cls.ArrayHelper.remove(keyActionIdArray, action.id);
        if (keyActionIdArray.length === 0) {
          this._boundActions.delete(key);
        }
      }
    }

    /**
     * Get the action by its name
     * @param {?string} name of the action
     * @returns {classes.NodeBase} action node
     */
    getAction(name) {
      return this._actions.get(name);
    }

    /**
     * Test if the action is registered
     * @param {string} name of the action
     * @returns {boolean} action node
     */
    hasAction(name) {
      return this._actions.has(name);
    }

    /**
     * Remove the action and unbind accelerators
     * @param {classes.ActionBase} action
     */
    destroyAction(action) {
      this._unbindAccelerators(action);

      let name = action.attribute("name");
      this._actions.delete(name);
      if (this._specialActions.has(name)) {
        this._specialActions.get(name).destroy();
        this._specialActions.delete(name);
      }

      let applicationWidget = this._application.getUI().getWidget();

      // Remove action from app contextMenu
      if (applicationWidget && applicationWidget.getContextMenu()) {
        let contextMenu = applicationWidget.getContextMenu();
        contextMenu.removeAndDestroyAction(name, false);
      }

      // Remove action from rowbound
      if (applicationWidget && applicationWidget.getRowBoundMenu()) {
        let rowBoundWidget = applicationWidget.getRowBoundMenu();
        rowBoundWidget.removeAndDestroyAction(name, false);
      }

      // Remove the action from its chrome location
      action.removeListener(context.constants.widgetEvents.activate);
      action.removeListener(context.constants.widgetEvents.disable);
      if (action.getWidget()) {
        if (this.isBackAction(action)) {
          context.ChromeActionService.removeBackAction(action.getWidget());
        } else {
          context.ChromeActionService.removeChromeAction(action.getWidget());
        }
      }
    }

    /**
     * Remove the actionDefault and unbind accelerators
     * @param {classes.NodeBase} action
     */
    destroyActionDefault(action) {
      this._unbindAccelerators(action);

      const acceleratorName = action.attribute("name");
      this._actionDefaults.delete(acceleratorName);
    }

    /**
     * Send a keyEvent to VM
     * @param {String} vmKey
     * @param {classes.NodeBase} [node] if specified send current cursors and value of this node
     */
    sendKey(vmKey, node = null) {
      const vmEvent = new cls.VMKeyEvent(vmKey);

      if (node) {
        const ctrl = node.getController();
        if (ctrl) {
          ctrl.sendWidgetCursors();
          ctrl.sendWidgetValue();
        }
      }
      this._application.scheduler.eventVMCommand(vmEvent);
    }

    /**
     * Execute an action by knowing its name
     * @param {string} name of the action
     * @param {Object} [options]
     * @returns {classes.CommandBase} the created command to execute the action
     */
    executeActionByName(name, options = {}) {
      const actionNode = this.getAction(name);
      return this.executeAction(actionNode, options);
    }

    /**
     * Execute an action with its ID
     * @param {number} idRef of the action
     * @param {Object} [options]
     * @returns {classes.CommandBase} the created command to execute the action
     */
    executeActionById(idRef, options = {}) {
      const actionNode = this._application.getNode(idRef);
      return this.executeAction(actionNode, options);
    }

    /**
     * Execute the action linked to the Node
     * Even if the action is not active
     * @param {classes.NodeBase} actionNode
     * @param {Object} [options]
     * @returns {classes.CommandBase} the created command to execute the action
     */
    executeAction(actionNode, options = {}) {
      if (!actionNode) {
        return null;
      }

      if (options.sendValue) {
        const sendValueNode = options.sendValueNode || this._application.getFocusedVMNodeAndValue(true);
        const sendValueCtrl = sendValueNode?.getController();
        if (sendValueCtrl) {
          const isDialogTouchedAction = actionNode.attribute("name") === "dialogtouched";
          // Send value only if node is in input mode
          // Don't send value if a dropDown is opened for the widget (expect for dialogtouched)
          const reallySendValue = sendValueCtrl.isInputKindDialogType() && (!sendValueCtrl.hasActiveDropDown() || isDialogTouchedAction);
          if (reallySendValue) {
            const group = this._application.scheduler.startGroupCommand();
            sendValueCtrl.sendWidgetCursors();
            sendValueCtrl.sendWidgetValue(options.newValue);
            this._application.scheduler.actionVMCommand(actionNode, {
              noUserActivity: Boolean(options.noUserActivity)
            });
            this._application.scheduler.finishGroupCommand();
            // when we execute an action, make sure dropdown and sidebar don't keep open
            if (!options.noUserActivity) {
              context.MainContainerService.hideFloatingElements();
            }
            return group;
          }
        }
      }
      // when we execute an action, make sure dropdown and sidebar don't keep open
      if (!options.noUserActivity) {
        context.MainContainerService.hideFloatingElements();
      }
      // push action command
      return this._application.scheduler.actionVMCommand(
        actionNode,
        {
          noUserActivity: Boolean(options.noUserActivity)
        },
        options.priority
      );
    }

    /**
     * Execute a local action
     * @param {String} actionName
     * @returns {boolean} true if local action has been processed
     */
    executeLocalAction(actionName) {
      context.LogService.keyboard.log("executeLocalAction: actionName: ", actionName);

      if (!actionName) {
        return false;
      }

      let processed = false;

      if (cls.ActionApplicationService.browserNativeActions.includes(actionName)) {
        context.LogService.keyboard.log("processKey found copy/cut/paste Action");
        // do nothing, for browser native actions (should be managed by the browser itself)
        return false;
      }

      const focusedNode = this._application.getFocusedVMNodeAndValue(true);

      let widget = null;
      let sendKey = false;

      switch (actionName) {
        case "guisnapshot":
          if (gbc.DebugService.isActive() || gbc.LogService.isRecordingEnabled()) {
            this._sendLogEvent("snapshot");
            processed = true;
          }
          break;
        case "nextfield":
        case "prevfield":
          // ignore these local actions for Table in DisplayArray
          sendKey = !(focusedNode.getTag().startsWith("Table") && focusedNode.attribute("dialogType") === "DisplayArray");
          break;
        case "prevpage":
        case "nextpage":
          widget = focusedNode.getWidget();
          // For table, widget process action
          if (widget?.getTableWidgetBase()) {
            widget.getTableWidgetBase().manageLocalAction(actionName);
            processed = true;
          } else if (widget?.getDialogType() === "InputArray" || !focusedNode.getTag().startsWith("FormField")) {
            // For other non-formfield widgets, send key to VM
            sendKey = true;
          }
          break;
        case "prevtab":
        case "nexttab":
          widget = focusedNode.getWidget();
          if (widget?.getFolderWidget()) {
            widget.getFolderWidget().manageLocalAction(actionName);
            processed = true;
          }
          break;
      }

      if (!processed && sendKey) {
        this.sendKey(cls.ActionApplicationService.getLocalActionAccelerator(actionName), focusedNode);
        processed = true;
      }

      return processed;
    }

    registerInterruptWidget(widget) {
      this._interruptWidgets.push(widget);
      widget.setInterruptableActive(this._application.isProcessing());
    }

    unregisterInterruptWidget(widget) {
      widget.setInterruptableActive(false);
      cls.ArrayHelper.remove(this._interruptWidgets, widget);
    }

    setInterruptablesActive(isActive) {
      for (const element of this._interruptWidgets) {
        element.setInterruptableActive(isActive);
      }
    }

    /**
     * Return the action node of active dialog for a given action name
     * @param actionName the name of the action
     * @returns {?classes.NodeBase} an action node
     */
    getActiveDialogActionForName(actionName) {
      const uiNode = this._application.uiNode();
      if (uiNode) {
        const window = this._application.getVMWindow();
        if (window) {
          // search the action in the current dialog
          const dialog = window.getActiveDialog();
          if (dialog) {
            const actions = dialog.getChildren();
            for (const element of actions) {
              const action = element;
              const isActive = action.attribute("active") !== 0;
              if (isActive) {
                if (action.attribute("name") === actionName) {
                  return action;
                }
              }
            }
          }
        }
      }
      return null;
    }

    /**
     * Find an action matching a key in a list of action nodes, respecting accelerator priority
     * (acceleratorName > acceleratorName2 > acceleratorName3 > acceleratorName4).
     * @param {classes.NodeBase[]} actions - action nodes to search in
     * @param {String} vmKey - VM key string (lowercased)
     * @param {Object} options
     * @param {Boolean} options.requireActive - only consider actions with attribute("active") !== 0
     * @param {Boolean} options.firstMatch - when true, return the first match at a given priority level; when false, return the last
     * @returns {?classes.NodeBase} matching action, or null
     * @private
     */
    _findActionByAcceleratorKey(actions, vmKey, { requireActive, firstMatch }) {
      const matches = (action, attrName) => {
        if (requireActive && action.attribute("active") === 0) {
          return false;
        }
        return action.attribute(attrName)?.toString().toLowerCase() === vmKey;
      };
      for (const attrName of ["acceleratorName", "acceleratorName2", "acceleratorName3", "acceleratorName4"]) {
        const found = firstMatch ? actions.find((a) => matches(a, attrName)) : actions.findLast((a) => matches(a, attrName));
        if (found) {
          return found;
        }
      }
      return null;
    }

    /**
     * Return the action node of active dialog according to vmKey param
     * @param {String} vmKey
     * @returns {classes.ActionNode} action node
     */
    getActiveDialogActionForKey(vmKey) {
      const dialog = this._application.getVMWindow()?.getActiveDialog();
      if (!dialog) {
        return null;
      }
      return /** @type {classes.ActionNode} */ this._findActionByAcceleratorKey(dialog.getChildren(), vmKey, {
        requireActive: true,
        firstMatch: false
      });
    }

    /**
     * Return the action node of default action list according to vmKey param
     * @param {String} vmKey
     * @param {classes.NodeBase} containerNode - node where to search for default action (FORM or UI)
     * @returns {classes.ActionDefaultNode} action default node
     */
    getDefaultActionForKey(vmKey, containerNode) {
      const actionDefaultList = containerNode?.getFirstChild("ActionDefaultList");
      if (!actionDefaultList) {
        return null;
      }
      return /** @type {classes.ActionDefaultNode} */ this._findActionByAcceleratorKey(actionDefaultList.getChildren(), vmKey, {
        requireActive: false,
        firstMatch: true
      });
    }

    /**
     * Return default action with provided name and optionally declared in form node
     * @param {String} name
     * @param {classes.NodeBase} containerNode - node where to search for default action (FORM or UI)
     * @returns {classes.ActionDefaultNode} action default node
     */
    getDefaultActionForName(name, containerNode) {
      const actionDefaultList = containerNode.getFirstChild("ActionDefaultList");
      if (actionDefaultList) {
        const actionDefaults = actionDefaultList.getChildren();
        for (const actionDefault of actionDefaults) {
          if (name === actionDefault.attribute("name")) {
            return /** @type {classes.ActionDefaultNode} */ actionDefault;
          }
        }
      }
      return null;
    }

    /**
     * Sends a log event command
     * @param {string} text you want to add to your log
     * @private
     */
    _sendLogEvent(text) {
      // Flash app and display message
      this._application.getUI().getWidget().flash(300);
      const messageService = this._application.message;
      const userInterfaceNode = this._application.getNode(0);
      const userInterfaceWidget = userInterfaceNode.getController().getWidget();
      const msgWidget = userInterfaceWidget.getMessageWidget();
      msgWidget.setText(gbc.I18NService.t("gwc.app.snapshot"));
      msgWidget.addClasses("warning");
      msgWidget.setHidden(false);
      messageService.addMessage("guisnapshot", msgWidget);
      messageService.handlePositions();
      this._registerTimeout(function () {
        msgWidget.setHidden(true);
      }, 3000);

      let jsonData = {
        type: text
      };
      this._application.scheduler.eventVMCommand(new cls.VMLogEvent(JSON.stringify(jsonData)));
    }
  }
  cls.ActionApplicationService = ActionApplicationService;
  cls.ApplicationServiceFactory.register("Action", cls.ActionApplicationService);
});
